'use client';

import { c as _c } from "react/compiler-runtime";
import { BuilderBlocks } from '@builder.io/react';
import { RadioGroup } from '@headlessui/react';
import { useState } from 'react';
import { Button } from '@src/components/Button/Button';
import { SKUSelectorV2Dropdown } from '@src/components/SKUSelectorV2/SKUSelectorV2Dropdown';
import { useLoadings } from '@src/hooks/useLoadings';
import { useBreakpoints } from '@src/utils/breakpoints.util';
import type { SKUSelectorV2Props, SKUSelectorV2ShopifyFields } from './SKUSelectorV2Props';
import { SKUSelectorV2Radio } from './SKUSelectorV2Radio';
const getDefaultSku = (props: SKUSelectorV2Props) => {
  const {
    style,
    radio,
    dropdown,
    pill
  } = props;
  if (style === 'radio') {
    return radio?.options?.[0]?.isDropdown ? radio?.options?.[0]?.dropdownOptions?.[0]?.sku ?? '' : radio?.options?.[0]?.sku ?? '';
  }
  if (style === 'dropdown') {
    return dropdown?.options?.[0]?.sku ?? '';
  }
  return pill?.options?.[0]?.sku ?? '';
};

/**
 * Returns Shopify variant/subscription fields for the selected SKU, per active environment.
 */
const getShopifyFields = (props: SKUSelectorV2Props, selectedSku: string): SKUSelectorV2ShopifyFields => {
  const {
    style,
    radio,
    dropdown,
    pill
  } = props;
  let option: SKUSelectorV2ShopifyFields | undefined;
  if (style === 'radio') {
    for (const radioOption of radio?.options ?? []) {
      if (radioOption.isDropdown) {
        option = radioOption.dropdownOptions?.find(o => o.sku === selectedSku);
      } else if (radioOption.sku === selectedSku) {
        option = radioOption;
      }
      if (option) break;
    }
  } else if (style === 'dropdown') {
    option = dropdown?.options?.find(o => o.sku === selectedSku);
  } else {
    option = pill?.options?.find(o => o.sku === selectedSku);
  }
  return {
    variantId: option?.variantId,
    subscriptionPlanId: option?.subscriptionPlanId,
    variantIdStage: option?.variantIdStage,
    subscriptionPlanIdStage: option?.subscriptionPlanIdStage,
    variantIdProduction: option?.variantIdProduction,
    subscriptionPlanIdProduction: option?.subscriptionPlanIdProduction
  };
};
export function SKUSelectorV2(props) {
  const $ = _c(43);
  const {
    style
  } = props;
  let t0;
  if ($[0] !== props) {
    t0 = getDefaultSku(props);
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [selectedSku, setSelectedSku] = useState(t0);
  const isLoading = useLoadings().loadings.isAddingLineItems ?? false;
  const breakpoints = useBreakpoints();
  const isMobile = breakpoints.isMobile || breakpoints.isTablet;
  let t1;
  if ($[2] !== isLoading || $[3] !== props.builderBlock?.id || $[4] !== props.previewAllItems || $[5] !== props.radio?.options || $[6] !== selectedSku) {
    t1 = () => {
      const options = (props.radio?.options ?? []).map((option, index) => ({
        sku: option.sku ?? "",
        content: <BuilderBlocks blocks={option.content} parentElementId={props.builderBlock?.id} dataPath={`component.radio[${index}].content`} />,
        isDropdown: option.isDropdown,
        dropdownOptions: (option.dropdownOptions ?? []).map((dropdownOption, dropdownOptionIndex) => ({
          sku: dropdownOption.sku ?? "",
          content: <BuilderBlocks blocks={dropdownOption.content} parentElementId={props.builderBlock?.id} dataPath={`component.radio[${index}].dropdownOptions[${dropdownOptionIndex}].content`} />,
          contentTop: <BuilderBlocks blocks={dropdownOption.contentTop} parentElementId={props.builderBlock?.id} dataPath={`component.radio[${index}].dropdownOptions[${dropdownOptionIndex}].contentTop`} />
        }))
      }));
      return <RadioGroup className="relative flex shrink-0 flex-col gap-2" value={selectedSku} onChange={sku => sku && !isLoading && setSelectedSku(sku)}>{options.map((option_0, index_0) => <SKUSelectorV2Radio key={`${index_0}-${option_0.sku}`} option={option_0} selectedSku={selectedSku} isLoading={isLoading} previewAllItems={props.previewAllItems} onChange={setSelectedSku} />)}</RadioGroup>;
    };
    $[2] = isLoading;
    $[3] = props.builderBlock?.id;
    $[4] = props.previewAllItems;
    $[5] = props.radio?.options;
    $[6] = selectedSku;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const renderRadio = t1;
  let t2;
  if ($[8] !== isLoading || $[9] !== props.builderBlock?.id || $[10] !== props.dropdown?.options || $[11] !== props.previewAllItems || $[12] !== selectedSku) {
    t2 = () => {
      const options_0 = (props.dropdown?.options ?? []).map((option_1, index_1) => ({
        sku: option_1.sku ?? "",
        content: <BuilderBlocks blocks={option_1.content} parentElementId={props.builderBlock?.id} dataPath={`component.dropdown[${index_1}].content`} />,
        contentTop: <BuilderBlocks blocks={option_1.contentTop} parentElementId={props.builderBlock?.id} dataPath={`component.dropdown[${index_1}].contentTop`} />
      }));
      const activeOption = options_0.find(option_2 => option_2.sku === selectedSku);
      return <SKUSelectorV2Dropdown dropdownOptions={options_0} activeOption={activeOption} selectedSku={selectedSku} isLoading={isLoading} onChange={setSelectedSku} previewAllItems={props.previewAllItems} />;
    };
    $[8] = isLoading;
    $[9] = props.builderBlock?.id;
    $[10] = props.dropdown?.options;
    $[11] = props.previewAllItems;
    $[12] = selectedSku;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  const renderDropdown = t2;
  let t3;
  if ($[14] !== renderRadio || $[15] !== style) {
    t3 = style === "radio" && renderRadio();
    $[14] = renderRadio;
    $[15] = style;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] !== renderDropdown || $[18] !== style) {
    t4 = style === "dropdown" && renderDropdown();
    $[17] = renderDropdown;
    $[18] = style;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  const t5 = props.addToCartVariant ?? "primary";
  const t6 = isMobile ? "medium" : "large";
  let t7;
  if ($[20] !== props || $[21] !== selectedSku) {
    t7 = getShopifyFields(props, selectedSku);
    $[20] = props;
    $[21] = selectedSku;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== selectedSku || $[24] !== t7) {
    t8 = [{
      sku: selectedSku,
      quantity: 1,
      ...t7
    }];
    $[23] = selectedSku;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== props.redirectUrl || $[27] !== props.shopifyRedirectUrl || $[28] !== t8) {
    t9 = {
      skus: t8,
      redirectUrl: props.redirectUrl,
      shopifyRedirectUrl: props.shopifyRedirectUrl
    };
    $[26] = props.redirectUrl;
    $[27] = props.shopifyRedirectUrl;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const t10 = props.builderBlock?.id;
  let t11;
  if ($[30] !== props.addToCartContent || $[31] !== t10) {
    t11 = <BuilderBlocks blocks={props.addToCartContent} parentElementId={t10} dataPath="component.addToCartContent" />;
    $[30] = props.addToCartContent;
    $[31] = t10;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== isLoading || $[34] !== t11 || $[35] !== t5 || $[36] !== t6 || $[37] !== t9) {
    t12 = <Button className="mt-4 w-full" isLoading={isLoading} variant={t5} size={t6} actionName="addItemsToCart" addItemsToCart={t9}>{t11}</Button>;
    $[33] = isLoading;
    $[34] = t11;
    $[35] = t5;
    $[36] = t6;
    $[37] = t9;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== t12 || $[40] !== t3 || $[41] !== t4) {
    t13 = <div data-testid="sku-selector">{t3}{t4}{t12}</div>;
    $[39] = t12;
    $[40] = t3;
    $[41] = t4;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  return t13;
}